import React, { useState } from 'react';
import { Link, useParams, useNavigate, Navigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { PelicanLogo } from '../components/PelicanLogo';
import { ChevronLeft } from 'lucide-react';
import { getSubjectsForCurriculum, getCurriculum } from '../constants/curricula';

export const CurriculumPage = () => {
  const { curriculumName } = useParams();
  const navigate = useNavigate();
  const curriculum = getCurriculum(curriculumName);
  const [activeLevel, setActiveLevel] = useState(curriculum?.levels?.[0] || null);
  const subjects = curriculum ? getSubjectsForCurriculum(curriculum.name) : [];

  if (!curriculum) {
    return <Navigate to="/subjects" replace />;
  }

  const handleSubjectClick = (subjectName) => {
    const params = new URLSearchParams({ curriculum: curriculum.name, subject: subjectName });
    if (curriculum.levels && activeLevel) params.set('level', activeLevel);
    navigate(`/resources?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/subjects" className="inline-flex items-center gap-1 text-sm text-foreground/70 hover:text-primary mb-6" data-testid="back-to-curricula">
          <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
          Back to curricula
        </Link>

        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent border border-border mb-4">
            <PelicanLogo className="h-4 w-4 text-primary" strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-[0.1em] font-mono text-primary">Curriculum</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-3" data-testid="curriculum-title">{curriculum.name}</h1>
          <p className="text-base sm:text-lg text-foreground/75 max-w-2xl mx-auto leading-relaxed">{curriculum.description}</p>

          {curriculum.levels && (
            <div className="mt-6 inline-flex items-center gap-2 p-1 rounded-full border border-border bg-surface" data-testid="level-toggle">
              {curriculum.levels.map((lvl) => (
                <button
                  key={lvl}
                  onClick={() => setActiveLevel(lvl)}
                  data-testid={`level-btn-${lvl}`}
                  className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${
                    activeLevel === lvl
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-foreground/70 hover:text-foreground'
                  }`}
                >
                  {lvl}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="subjects-grid">
          {subjects.map((subject) => (
            <button
              key={subject.name}
              onClick={() => handleSubjectClick(subject.name)}
              data-testid={`curriculum-subject-${subject.name.toLowerCase().replace(/\s+/g, '-')}`}
              className="text-left"
            >
              <div className={`p-8 rounded-lg border ${subject.color} hover:-translate-y-1 transition-all duration-200 hover:shadow-md h-full w-full`}>
                <div className="text-5xl mb-4">{subject.icon}</div>
                <h3 className="text-xl font-semibold mb-2 tracking-tight">{subject.name}</h3>
                <p className="text-sm text-foreground/70 leading-relaxed">{subject.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
